/**
 * CR3 — picq. Public PIC questionnaire answering via magic-link (/q/:token).
 * Standalone (no AppShell), matches the picq gradient header. No login.
 *
 * Flow: verify+load (GET /q/:token/questionnaire) → answer each question
 * (scale/choice/open via QuestionField) → debounced autosave (PATCH
 * /q/:token/answers, "Tersimpan otomatis" indicator) → submit (POST
 * /q/:token/submit) with required-question validation → success state.
 *
 * Handles every link state: expired / invalid / used, already-submitted (server
 * returns submittedAt on saved answers, or 'already_submitted'), not-ready,
 * rate-limited.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Send,
  Loader2,
  Cloud,
  CloudOff,
} from 'lucide-react';
import type { AnswerInput, QuestionDTO } from '@klinik/shared';
import { Logo } from '../components/Logo';
import { Spinner } from '../components/ui/Spinner';
import { QuestionField, normalizeAnswerValue, isAnswered, type AnswerValue } from '../components/QuestionField';
import { fadeOnly, fadeRise, staggerContainer } from '../lib/motion';
import {
  getPicQuestionnaire,
  savePicAnswers,
  submitPicAnswers,
  useAutosave,
  friendlyError,
  type PicQuestionnaireData,
} from '../lib/questionnaires';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'submitted' }
  | { phase: 'ready'; data: PicQuestionnaireData };

export function PicQuestionnaire() {
  const { token = '' } = useParams();
  const reduce = useReducedMotion();
  const variants = reduce ? fadeOnly : fadeRise;

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [missingIds, setMissingIds] = useState<Set<string>>(new Set());
  // UU PDP: the PIC must explicitly accept the privacy policy before submitting.
  const [consent, setConsent] = useState(false);

  const { status: saveStatus, schedule, flush } = useAutosave((a) => savePicAnswers(token, a));
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getPicQuestionnaire(token);
      if (cancelled) return;
      if (!res.ok) {
        setState({ phase: 'error', message: friendlyError(res.error.code, res.error.message) });
        return;
      }
      const { questions, savedAnswers } = res.data;
      // If the questionnaire is already completed (or all saved answers were
      // submitted), show the success state — the link is done.
      const alreadySubmitted =
        res.data.questionnaire.status === 'completed' ||
        (savedAnswers.length > 0 && savedAnswers.every((a) => a.submittedAt));
      if (alreadySubmitted) {
        setState({ phase: 'submitted' });
        return;
      }
      const initial: Record<string, AnswerValue> = {};
      const byQ = new Map(savedAnswers.map((a) => [a.questionId, a.value]));
      for (const q of questions) {
        initial[q.id] = normalizeAnswerValue(q.type, byQ.get(q.id));
      }
      setAnswers(initial);
      setState({ phase: 'ready', data: res.data });
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const questions = state.phase === 'ready' ? state.data.questions : [];

  const answeredCount = useMemo(
    () => questions.filter((q) => isAnswered(q.type, answers[q.id] ?? null)).length,
    [questions, answers],
  );
  const total = questions.length;
  const progressPct = total === 0 ? 0 : Math.round((answeredCount / total) * 100);

  function buildPayload(map: Record<string, AnswerValue>): AnswerInput[] {
    return questions.map((q) => ({ questionId: q.id, value: map[q.id] ?? null }));
  }

  function onChange(q: QuestionDTO, value: AnswerValue) {
    setAnswers((prev) => {
      const next = { ...prev, [q.id]: value };
      schedule(buildPayload(next));
      return next;
    });
    if (missingIds.has(q.id) && isAnswered(q.type, value)) {
      setMissingIds((prev) => {
        const n = new Set(prev);
        n.delete(q.id);
        return n;
      });
    }
  }

  async function onSubmit() {
    if (submitting) return;
    if (state.phase !== 'ready') return;
    setSubmitError(null);
    // UU PDP: refuse to submit without explicit consent (server also enforces it).
    if (!consent) {
      setSubmitError('Mohon menyetujui Kebijakan Privasi sebelum mengirim jawaban.');
      return;
    }
    // Client-side required validation (server is authoritative).
    const missing = new Set(
      questions.filter((q) => q.required && !isAnswered(q.type, answers[q.id] ?? null)).map((q) => q.id),
    );
    if (missing.size > 0) {
      setMissingIds(missing);
      setSubmitError(`Masih ada ${missing.size} pertanyaan wajib yang belum dijawab.`);
      const first = questions.find((q) => missing.has(q.id));
      if (first) document.getElementById(`pq-${first.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    await flush(); // make sure latest autosave landed first
    const res = await submitPicAnswers(token, buildPayload(answers), state.data.policyVersion);
    setSubmitting(false);
    if (res.ok) {
      setState({ phase: 'submitted' });
    } else if (res.error.code === 'already_submitted') {
      setState({ phase: 'submitted' });
    } else {
      setSubmitError(friendlyError(res.error.code, res.error.message));
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4 sm:px-8">
        <Logo />
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
          <ShieldCheck size={14} aria-hidden /> Tautan aman · tanpa login
        </span>
      </header>

      <main className="p-4 sm:p-8">
        <div className="mx-auto max-w-3xl">
          {state.phase === 'loading' ? (
            <div className="h-40">
              <Spinner label="Memverifikasi tautan…" />
            </div>
          ) : state.phase === 'error' ? (
            <div className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-red-700 shadow-soft">
              <AlertTriangle size={20} className="mt-0.5 shrink-0" aria-hidden />
              <div>
                <div className="font-semibold">Tautan tidak dapat dibuka</div>
                <div className="mt-0.5 text-sm">{state.message}</div>
              </div>
            </div>
          ) : state.phase === 'submitted' ? (
            <SubmittedState reduce={reduce} />
          ) : (
            <motion.div initial="hidden" animate="show" variants={staggerContainer}>
              {/* Gradient hero */}
              <motion.div
                variants={variants}
                className="rounded-2xl bg-gradient-to-r from-blue-700 to-sky-400 p-5 text-white shadow-soft"
              >
                <div className="text-sm text-white/85">Kuesioner General Check-Up untuk</div>
                <div className="font-display text-xl font-bold">
                  {state.data.picName ?? 'PIC'}
                  {state.data.division ? ` · Divisi ${state.data.division}` : ''}
                </div>
                <div className="mt-1 text-sm text-white/85">
                  Dikirim oleh {state.data.company.name}. Jawaban Anda membantu mendiagnosa kesehatan
                  bisnis. Jawaban tersimpan otomatis.
                </div>
              </motion.div>

              {/* Sticky progress + autosave indicator */}
              <motion.div
                variants={variants}
                className="sticky top-2 z-10 mt-4 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-2.5 shadow-soft backdrop-blur"
              >
                <div className="flex flex-1 items-center gap-3">
                  <span className="text-xs font-semibold text-slate-500">
                    {answeredCount} / {total}
                  </span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <motion.div
                      className="h-1.5 rounded-full bg-brand"
                      animate={{ width: `${progressPct}%` }}
                      transition={reduce ? { duration: 0 } : { duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                </div>
                <SaveIndicator status={saveStatus} />
              </motion.div>

              {/* Questions */}
              <div className="mt-4 space-y-3">
                {questions.map((q, i) => (
                  <motion.div key={q.id} variants={variants} id={`pq-${q.id}`}>
                    <div
                      className={
                        missingIds.has(q.id) ? 'rounded-xl ring-2 ring-red-300 ring-offset-2' : ''
                      }
                    >
                      <QuestionField
                        question={q}
                        index={i + 1}
                        value={answers[q.id] ?? null}
                        onChange={(v) => onChange(q, v)}
                        disabled={submitting}
                      />
                    </div>
                    {missingIds.has(q.id) && (
                      <div className="mt-1 pl-1 text-xs font-medium text-red-500">
                        Pertanyaan ini wajib dijawab.
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              {submitError && (
                <motion.div
                  variants={variants}
                  className="mt-4 flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700"
                >
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
                  <span>{submitError}</span>
                </motion.div>
              )}

              {/* UU PDP consent — REQUIRED checkbox; submit is disabled until checked. */}
              <motion.label
                variants={variants}
                className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-3.5 text-xs text-blue-900"
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
                  aria-label="Setujui Kebijakan Privasi"
                />
                <span>
                  Saya menyetujui <b>Kebijakan Privasi</b> termasuk transfer data ke sub-prosesor di
                  luar negeri (mis. layanan AI &amp; cloud).{' '}
                  <a
                    href="https://klinikbisnisnineco.id/kebijakan-privasi"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="font-semibold text-blue-600 underline"
                  >
                    Baca Kebijakan Privasi
                  </a>
                  {state.data.policyVersion && (
                    <span className="ml-1 text-blue-500">(v{state.data.policyVersion})</span>
                  )}
                </span>
              </motion.label>

              {/* Footer submit */}
              <motion.div
                variants={variants}
                className="mt-3 flex flex-wrap items-center justify-between gap-3"
              >
                <span className="text-xs text-slate-400">
                  Progres {answeredCount} / {total} · tersimpan otomatis
                </span>
                <button
                  onClick={() => void onSubmit()}
                  disabled={submitting || !consent}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <Loader2 size={16} className="animate-spin" aria-hidden />
                  ) : (
                    <Send size={16} aria-hidden />
                  )}
                  {submitting ? 'Mengirim…' : 'Kirim Jawaban'}
                </button>
              </motion.div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}

function SaveIndicator({ status }: { status: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (status === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
        <Loader2 size={13} className="animate-spin" aria-hidden /> Menyimpan…
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-red-500" role="status">
        <CloudOff size={13} aria-hidden /> Gagal menyimpan
      </span>
    );
  }
  if (status === 'saved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-green-600" role="status">
        <Cloud size={13} aria-hidden /> Tersimpan otomatis
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
      <Cloud size={13} aria-hidden /> Tersimpan otomatis
    </span>
  );
}

function SubmittedState({ reduce }: { reduce: boolean | null }) {
  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-green-100 bg-white p-8 text-center shadow-soft"
    >
      <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-50 text-green-600">
        <CheckCircle2 size={34} aria-hidden />
      </span>
      <div className="mt-4 font-display text-xl font-bold text-slate-900">Terima kasih!</div>
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
        Jawaban Anda sudah terkirim ke tim Nine Co dan akan digunakan untuk menyusun Laporan General
        Check-Up. Anda dapat menutup halaman ini.
      </p>
      <div className="mx-auto mt-4 inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
        <ShieldCheck size={14} aria-hidden /> Jawaban terenkripsi &amp; aman
      </div>
    </motion.div>
  );
}
